import { UseFormReturn } from "react-hook-form";
import { Form } from "@themes/pollinator/ui/Form";
import { FormField } from "@themes/pollinator/ui/FormField";
import { ActionButtonsGroup } from "@themes/pollinator/ui/ActionButtonsGroup";
import {
  AccountFormBase,
  type AccountFormBaseProps,
  type AccountWithPhoneFormData,
} from "@core/features/profile";

export type AccountFormData = AccountWithPhoneFormData;
export type { AccountWithPhoneFormData };

export type Props = Omit<
  AccountFormBaseProps,
  "renderForm" | "renderActions"
>;

/**
 * Presentational component for the account/email update form.
 * Receives all data and callbacks via props.
 */
export function AccountForm({
  form,
  onSubmit,
  onCancel,
  isDirty,
  isValid,
  isSubmitting,
  ...baseProps
}: Props) {
  return (
    <AccountFormBase
      form={form}
      onSubmit={onSubmit}
      onCancel={onCancel}
      isDirty={isDirty}
      isValid={isValid}
      isSubmitting={isSubmitting}
      renderForm={({ form }: { form: UseFormReturn<AccountFormData> }) => (
        <Form form={form} errorContainerClassName="p-0 w-full max-w-full mt-8">
          <FormField
            name="email"
            label="Email"
            component="Input"
            placeholder="Enter your email"
            autoComplete="username"
            layout="horizontal"
            required
          />
          <FormField
            name="phoneNumber"
            label="Phone Number"
            component="PhoneInput"
            placeholder="Enter your phone number"
            layout="horizontal"
          />
          <FormField
            name="password"
            label="Password"
            component="Input"
            type="password"
            placeholder="Enter your current password"
            autoComplete="current-password"
            layout="horizontal"
            required
          />
        </Form>
      )}
      renderActions={({
        onSubmit,
        onCancel,
        isDirty,
        isValid,
        isSubmitting,
      }) => (
        <ActionButtonsGroup
          primaryButtonText={isSubmitting ? "Saving..." : "Save"}
          onPrimaryClick={onSubmit}
          secondaryButtonText={isDirty ? "Cancel" : undefined}
          onSecondaryClick={isDirty ? onCancel : undefined}
          className="size-max w-fit self-end"
          primaryButtonProps={{
            disabled: !isDirty || isSubmitting,
            visuallyDisabled: isDirty && !isSubmitting && !isValid,
          }}
        />
      )}
      {...baseProps}
    />
  );
}

export default AccountForm;
